import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileCheck2, Plus, Trash2 } from 'lucide-react';
import { financialYearOf, type Lut } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useOrgSettings, useToday } from '@/hooks/useOrgSettings';
import { useScrollToHash } from '@/hooks/useScrollToHash';
import { formatDate } from '@/lib/dates';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Spinner } from '@/features/common/ui';

/**
 * Letters of Undertaking, one per financial year.
 *
 * ── WHY IT IS A SETTING AND NOT A FIELD ON THE INVOICE ──
 * An LUT is filed once a year (GST RFD-11) and covers every export of that
 * year; typing its ARN onto each invoice is forty chances to type it wrong.
 * Recorded here, the server prints it on every export invoice dated inside
 * the year, and the review flags an export that has none.
 *
 * Saved as the whole list under `luts`. The settings PATCH merges top-level
 * keys, so sending only `luts` leaves the tax card's fields alone - and the
 * list is short enough (one a year) that replacing it is simpler than an
 * add/remove API.
 *
 * Anchored at `#luts`, because the review's "Record the LUT" links here.
 */
export function LutsCard() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const settings = useOrgSettings();
  const today = useToday();
  const [financialYear, setFinancialYear] = useState(() => financialYearOf(today));
  const [arn, setArn] = useState('');
  const [filedOn, setFiledOn] = useState('');

  useScrollToHash('luts', !settings.isLoading);

  const luts = [...(settings.data?.luts ?? [])].sort((a, b) => b.financialYear.localeCompare(a.financialYear));
  const canManage = perms.can('settings', 'manage');

  const save = useMutation({
    mutationFn: (next: Lut[]) => api.patch('/settings', { luts: next }),
    onSuccess: () => {
      setArn('');
      setFiledOn('');
      void queryClient.invalidateQueries({ queryKey: ['settings', org?.id] });
      // The ARN prints on the invoice and the register reads it, and a
      // missing one is a review finding: all three are stale now.
      void queryClient.invalidateQueries({ queryKey: ['invoice-document', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['exports', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['review', org?.id] });
    },
  });

  const error = save.error instanceof ApiError ? save.error : undefined;
  // The server validates the whole array, so the field it blames carries an
  // index. The new row is always the last one sent.
  const newIndex = luts.length;
  const fieldError = (field: keyof Lut) => error?.fieldError(`luts.${newIndex}.${field}`);
  const duplicate = luts.some((lut) => lut.financialYear === financialYear.trim());

  return (
    <Card id="luts" className="scroll-mt-4" data-testid="luts-card">
      <CardHeader>
        <CardTitle>
          <FileCheck2 className="size-4 text-muted-foreground" aria-hidden />
          Letters of Undertaking
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          An export goes out without IGST only under an LUT filed for that financial year (GST RFD-11).
          The ARN prints on every export invoice dated in the year.
        </p>

        {settings.isLoading ? (
          <Spinner />
        ) : luts.length ? (
          <ul className="space-y-2">
            {luts.map((lut) => (
              <li
                key={lut.financialYear}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3"
                data-testid="lut-row"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">FY {lut.financialYear}</p>
                  <p className="break-all font-mono text-xs">{lut.arn}</p>
                  {lut.filedOn ? <p className="text-xs text-muted-foreground">Filed {formatDate(lut.filedOn)}</p> : null}
                </div>
                {canManage ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={save.isPending}
                    aria-label={`Remove the LUT for ${lut.financialYear}`}
                    onClick={() => {
                      if (!window.confirm(`Remove the LUT for ${lut.financialYear}? Export invoices of that year stop printing its ARN.`)) return;
                      save.mutate(luts.filter((other) => other.financialYear !== lut.financialYear));
                    }}
                  >
                    <Trash2 className="size-4" aria-hidden />
                    Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No LUT recorded yet.</p>
        )}

        {canManage ? (
          <form
            className="space-y-3 rounded-md border border-border p-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!arn.trim() || duplicate) return;
              save.mutate([
                ...luts,
                { financialYear: financialYear.trim(), arn: arn.trim().toUpperCase(), ...(filedOn ? { filedOn } : {}) },
              ]);
            }}
          >
            <div className="grid gap-3 [&>*]:min-w-0 sm:grid-cols-3">
              <Input
                label="Financial year"
                value={financialYear}
                onChange={(event) => setFinancialYear(event.target.value)}
                placeholder="2026-27"
                inputMode="numeric"
                maxLength={7}
                error={duplicate ? `There is already an LUT for ${financialYear.trim()}.` : fieldError('financialYear')}
              />
              <Input
                label="ARN"
                value={arn}
                onChange={(event) => setArn(event.target.value.toUpperCase())}
                placeholder="AD330326012345X"
                className="font-mono"
                maxLength={30}
                error={fieldError('arn')}
              />
              <Input
                label="Filed on"
                type="date"
                value={filedOn}
                onChange={(event) => setFiledOn(event.target.value)}
                error={fieldError('filedOn')}
              />
            </div>
            {error && !fieldError('financialYear') && !fieldError('arn') && !fieldError('filedOn') ? (
              <p role="alert" className="text-sm text-destructive">{error.message}</p>
            ) : null}
            <Button type="submit" size="sm" disabled={!arn.trim() || duplicate || save.isPending}>
              <Plus className="size-4" aria-hidden />
              Add LUT
            </Button>
          </form>
        ) : (
          <p className="text-xs text-muted-foreground">Only an administrator can change these.</p>
        )}
      </CardContent>
    </Card>
  );
}
